import React from "react";
import { TrendingUp, Tag, History, Calculator } from "lucide-react";
import { money } from "@/components/shared";

const sqftNum = (s) => {
  const n = parseFloat(String(s ?? "").replace(/[^0-9.]/g, ""));
  return isNaN(n) ? null : n;
};

export function compsStats(sold = []) {
  const prices = (sold || []).map((s) => Number(s.sold_price)).filter((n) => n > 0);
  const ppsfs = (sold || [])
    .map((s) => {
      const p = Number(s.sold_price);
      const sf = sqftNum(s.sqft);
      return p > 0 && sf ? p / sf : null;
    })
    .filter((x) => x != null);
  const avg = prices.length ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length) : null;
  const sorted = [...prices].sort((a, b) => a - b);
  const median = sorted.length
    ? sorted.length % 2
      ? sorted[(sorted.length - 1) / 2]
      : Math.round((sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2)
    : null;
  const ppsf = ppsfs.length ? Math.round(ppsfs.reduce((a, b) => a + b, 0) / ppsfs.length) : null;
  return { avg, median, ppsf, count: prices.length };
}

const bdbapk = (s) => [s.beds, s.baths, s.parking].map((x) => x || "—").join(" / ");
const hasResale = (r) =>
  r && ((r.sold || []).length || (r.for_sale || []).length || r.summary || r.listing_history || r.est_resale_range);

export function ResaleSection({ resale, showEmpty = false }) {
  if (!hasResale(resale)) {
    if (!showEmpty) return null;
    return (
      <div className="mt-4 p-4 rounded-xl bg-forest/5 border border-forest/20" data-testid="resale-section">
        <p className="text-sm font-semibold text-forest flex items-center gap-1.5">
          <TrendingUp className="h-4 w-4" /> Resale &amp; Recent Sales
        </p>
        <p className="text-sm text-muted-foreground mt-2" data-testid="resale-coming-soon">Recent sales coming soon.</p>
      </div>
    );
  }
  const sold = resale.sold || [];
  const stats = compsStats(sold);

  return (
    <div className="mt-4 p-4 rounded-xl bg-forest/5 border border-forest/20" data-testid="resale-section">
      <p className="text-sm font-semibold text-forest flex items-center gap-1.5">
        <TrendingUp className="h-4 w-4" /> Resale &amp; Recent Sales
      </p>

      {resale.est_resale_range && (
        <div className="mt-3 p-3 rounded-xl bg-forest/10 border border-forest/20" data-testid="resale-range">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-forest">Estimated resale range</p>
          <p className="font-heading text-xl font-bold mt-0.5">{resale.est_resale_range}</p>
        </div>
      )}

      {stats.count > 0 && (
        <p className="mt-3 text-xs text-foreground/80 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="resale-stats">
          <span className="inline-flex items-center gap-1 font-semibold"><Calculator className="h-3.5 w-3.5 text-forest" /> Sold comps ({stats.count})</span>
          <span>Average: <span className="font-semibold">{money(stats.avg)}</span></span>
          <span>Median: <span className="font-semibold">{money(stats.median)}</span></span>
          {stats.ppsf && <span>Avg price/sq ft: <span className="font-semibold">${stats.ppsf.toLocaleString()}</span></span>}
        </p>
      )}

      {sold.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs" data-testid="resale-sold-table">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-1.5 pr-3">Address</th>
                <th className="py-1.5 pr-3">Building</th>
                <th className="py-1.5 pr-3">Condition</th>
                <th className="py-1.5 pr-3">Bd / Ba / Pk</th>
                <th className="py-1.5 pr-3">Sq ft</th>
                <th className="py-1.5 pr-3">Sold</th>
                <th className="py-1.5">Closed</th>
              </tr>
            </thead>
            <tbody>
              {sold.map((s, i) => (
                <tr key={s.id || i} className="border-b border-border/50 align-top">
                  <td className="py-1.5 pr-3 font-medium">
                    {s.address || "—"}
                    {s.notes ? <span className="block font-normal text-muted-foreground">{s.notes}</span> : null}
                  </td>
                  <td className="py-1.5 pr-3">{s.building || "—"}</td>
                  <td className="py-1.5 pr-3">{s.condition || "—"}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap">{bdbapk(s)}</td>
                  <td className="py-1.5 pr-3">{s.sqft || "—"}</td>
                  <td className="py-1.5 pr-3 font-semibold">{s.sold_price ? money(s.sold_price) : "—"}</td>
                  <td className="py-1.5 whitespace-nowrap">{s.closed_date || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {resale.summary && <p className="text-xs text-foreground/80 mt-3">{resale.summary}</p>}

      {(resale.for_sale || []).length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
            <Tag className="h-3 w-3" /> Currently for sale nearby
          </p>
          <ul className="mt-1.5 space-y-1">
            {resale.for_sale.map((f, i) => (
              <li key={f.id || i} className="text-xs text-foreground/80">
                <span className="font-medium">{f.address || "—"}</span>
                {f.building ? ` (${f.building})` : ""}
                {f.list_price ? <> — <span className="font-semibold">{money(f.list_price)}</span></> : ""}
                {f.dom ? ` · ${f.dom} days on market` : ""}
                {f.notes ? ` · ${f.notes}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      {resale.listing_history && (
        <div className="mt-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
            <History className="h-3 w-3" /> Listing history
          </p>
          <p className="text-xs text-foreground/80 mt-1">{resale.listing_history}</p>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground mt-3 italic" data-testid="resale-disclaimer">
        Estimate only, based on recent sales in HiCentral MLS. Deemed reliable but not guaranteed. Not an appraisal.
      </p>
    </div>
  );
}
